import React, { useEffect, useMemo, useState } from "react";
import API_BASE from "./apiBase";
import "../css/cookie-responsive.css";
import {
  ShieldCheck, Search, Calendar, CheckCircle2, AlertTriangle, Globe, List,
  LayoutGrid, RefreshCw, Download, Plus, MoreVertical, Users, Monitor,
  Smartphone, ChevronDown, XCircle,
} from "lucide-react";
import CommonPagination from "./CommonPagination";

// Endpoint that returns DPDP Consent Banner records for the tenant.
// If your backend exposes this data under a different path, only this constant needs to change.
const DPDP_CONSENT_ENDPOINT = "/user/dpdp-consents";

/* Browser Brand Icons */
const ChromeIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className}>
    <path fill="#4CAF50" d="M24 44c8.3 0 15.5-5.1 18.5-12.4l-10-17.3c-.9 1.5-2.2 2.8-3.8 3.7l-9.3 16.1c1.5.6 3.1.9 4.6.9z" />
    <path fill="#FFC107" d="M44 24c0-2.3-.4-4.5-1.1-6.5l-20.1.1c1.7.3 3.3 1.1 4.5 2.3l11.4 19.7C42.2 35.5 44 30 44 24z" />
    <path fill="#FF3D00" d="M24 4C14.7 4 6.8 9.9 4 18.1l10.3 17.8c1.3-1.2 2.8-2.1 4.5-2.6L24 16h18.9C39.5 8.9 32.3 4 24 4z" />
    <circle cx="24" cy="24" r="10" fill="#FFFFFF" />
    <circle cx="24" cy="24" r="8" fill="#1976D2" />
  </svg>
);

const EdgeIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="none">
    <defs>
      <linearGradient id="edge_cbn_a" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#0C59A4" />
        <stop offset="100%" stopColor="#114A8B" />
      </linearGradient>
      <linearGradient id="edge_cbn_b" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#0078D7" />
        <stop offset="60%" stopColor="#00BCF2" />
        <stop offset="100%" stopColor="#2CD889" />
      </linearGradient>
    </defs>
    <path d="M22 12c0 5.52-4.48 10-10 10A10 10 0 0 1 2 12C2 6.48 6.48 2 12 2c.28 0 .5.22.5.5 0 2-1 4.5-3.5 5.5-2.2 1-3.5 2.5-3.5 4 0 3.04 2.46 5.5 5.5 5.5 2.8 0 5-2 5.5-4.5.1-.5.5-.9 1-.9H21.5c.28 0 .5.22.5.5V12z" fill="url(#edge_cbn_a)" />
    <path d="M12 22c5.52 0 10-4.48 10-10 0-4.5-3-8.5-7.5-9.5-.3 0-.5.3-.5.6 0 2-1 4.5-3 6-1.8 1.4-2.5 3.4-2 5.4.8 3.5 3.8 7.5 3 7.5z" fill="url(#edge_cbn_b)" />
  </svg>
);

const FirefoxIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <defs>
      <linearGradient id="ff_cbn_bg" x1="4" y1="4" x2="44" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FF9400" />
        <stop offset="45%" stopColor="#FF2A54" />
        <stop offset="100%" stopColor="#7622FF" />
      </linearGradient>
      <linearGradient id="ff_cbn_flame" x1="12" y1="12" x2="36" y2="36" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#FFE600" />
        <stop offset="100%" stopColor="#FF6600" />
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="20" fill="url(#ff_cbn_bg)" />
    <path d="M33 16c-3-3-7.5-4.5-12-4.5-6.5 0-12 5.5-12 12 0 2.3.6 4.4 1.7 6.3 3-3 5-7.3 5-12 0-1-.1-2-.4-3 3.5 2.5 6 6.5 6 11 0 4-1.8 7.5-4.5 10 3.8 2 8.2 3.1 12.8 3.1 9.2 0 16.7-7.5 16.7-16.7 0-3.3-1-6.4-2.7-9-1.6 3-4.6 5.1-8 5.5-1.3-2.4-2.5-5.3-2.6-8.7z" fill="url(#ff_cbn_flame)" />
  </svg>
);

const SafariIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <defs>
      <linearGradient id="saf_cbn_bg" x1="24" y1="4" x2="24" y2="44" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#2BA6FF" />
        <stop offset="100%" stopColor="#0062DF" />
      </linearGradient>
    </defs>
    <circle cx="24" cy="24" r="20" fill="url(#saf_cbn_bg)" stroke="#FFFFFF" strokeWidth="2" />
    <polygon points="24,10 28,24 24,38 20,24" fill="#FF3B30" />
    <polygon points="24,24 28,24 24,38 20,24" fill="#FFFFFF" />
    <polygon points="24,10 20,24 24,24" fill="#D32F2F" />
    <polygon points="24,24 20,24 24,38" fill="#D1D5DB" />
    <circle cx="24" cy="24" r="2.5" fill="#FFFFFF" />
  </svg>
);

const OperaIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <path d="M24 4C13 4 4 13 4 24s9 20 20 20 20-9 20-20S35 4 24 4zm0 6.5c6 0 10 6 10 13.5s-4 13.5-10 13.5-10-6-10-13.5 4-13.5 10-13.5z" fill="#FF1B2D" />
  </svg>
);

const BraveIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <path d="M24 4L6 11v11c0 10.5 7.6 18 18 22 10.4-4 18-11.5 18-22V11L24 4zm0 7l12 5v7c0 7-5 12.6-12 15.6-7-3-12-8.6-12-15.6v-7l12-5z" fill="#FB542B" />
    <path d="M24 16l8 3.5v4.5c0 5-3.5 9-8 11.2-4.5-2.2-8-6.2-8-11.2V24l8-8z" fill="#FF7A00" opacity="0.6" />
  </svg>
);

const SamsungIcon = ({ size = 20, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" className={className} fill="none">
    <circle cx="24" cy="24" r="16" fill="#1C69D4" />
    <ellipse cx="24" cy="24" rx="20" ry="8" fill="none" stroke="#7C3AED" strokeWidth="3.5" transform="rotate(-30 24 24)" />
  </svg>
);

const getBrowserComponent = (name = "") => {
  const n = String(name || "").toLowerCase().trim();
  if (n.includes("edge") || n.includes("edg")) return EdgeIcon;
  if (n.includes("chrome") || n.includes("crios")) return ChromeIcon;
  if (n.includes("firefox") || n.includes("fxios")) return FirefoxIcon;
  if (n.includes("safari")) return SafariIcon;
  if (n.includes("opera") || n.includes("opr")) return OperaIcon;
  if (n.includes("brave")) return BraveIcon;
  if (n.includes("samsung")) return SamsungIcon;
  return null;
};

const DATE_RANGES = [
  { value: "all", label: "All time", days: null },
  { value: "today", label: "Today", days: 1 },
  { value: "7", label: "Last 7 days", days: 7 },
  { value: "30", label: "Last 30 days", days: 30 },
  { value: "90", label: "Last 90 days", days: 90 },
];

function CookiesNotifyPage({ user }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [view, setView] = useState("list"); // 'list' (table) | 'grid' (cards)
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [expandedId, setExpandedId] = useState(null);

  const isConsentBannerRoute =
    typeof window !== "undefined" && /\/user\/consent-banner(\/|$)/.test(window.location.pathname || "");
  const [typeFilter] = useState(isConsentBannerRoute ? "dpdp" : "all");

  /* ------------------------------ helpers ------------------------------ */
  const getTenantId = () =>
    window.USER && window.USER.tenant_id
      ? Number(window.USER.tenant_id)
      : Number(localStorage.getItem("tenant_id")) || null;

  const getRecordIp = (r) => r.ip || r.ip_address || r.remote_addr || r.source_ip || r.remoteIp || "";
  const getRecordUA = (r) => r.user_agent || r.ua || r.userAgent || r.browser || "";

  const parseDate = (s) => {
    if (!s) return null;
    try {
      const str = String(s).trim();
      const isoNaive = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
      const d = isoNaive.test(str) ? new Date(str.replace(" ", "T") + "Z") : new Date(str);
      return isNaN(d.getTime()) ? null : d;
    } catch (e) {
      return null;
    }
  };

  // dd/mm/yyyy, hh:mm:ss (IST)
  const formatDate = (d) => {
    if (!d) return "";
    try {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Kolkata", hour12: false,
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit", second: "2-digit",
      }).formatToParts(d).reduce((a, p) => ({ ...a, [p.type]: p.value }), {});
      return `${parts.day}/${parts.month}/${parts.year}, ${parts.hour}:${parts.minute}:${parts.second}`;
    } catch (e) {
      return d.toString();
    }
  };

  const getExpires = (r) => {
    if (r.expires_at) {
      const e = parseDate(r.expires_at);
      if (e) return e;
    }
    const created = parseDate(r.created_at);
    return created ? new Date(created.getTime() + 30 * 24 * 60 * 60 * 1000) : null;
  };

  const isExpired = (r) => {
    const e = getExpires(r);
    return e ? new Date() > e : false;
  };

  const hostOf = (url, fallback = "unknown") => {
    try { return new URL(url).hostname || fallback; } catch (e) { return fallback; }
  };

  // Browser pehle, phir OS. Example: "Edge 153 • Windows 10/11"
  const parseUA = (ua = "") => {
    const s = String(ua || "").trim();
    if (!s) return { label: "Unknown browser", mobile: false, browser: "", browserName: "", os: "" };

    let os = "";
    if (/Windows NT 10/i.test(s)) os = "Windows 10/11";
    else if (/Windows/i.test(s)) os = "Windows";
    else if (/Android/i.test(s)) os = "Android";
    else if (/iPhone|iPad|iOS/i.test(s)) os = "iOS";
    else if (/Mac OS X|Macintosh/i.test(s)) os = "MacOS";
    else if (/Linux/i.test(s)) os = "Linux";

    // Order matters: Edge/Opera/Samsung UA mein "Chrome" aur "Safari" bhi hota hai
    const rules = [
      ["Edge", /(?:Edg|EdgA|EdgiOS|Edge)[\/ ]([\d.]+)/],
      ["Opera", /(?:OPR|Opera)[\/ ]([\d.]+)/],
      ["Samsung Internet", /SamsungBrowser[\/ ]([\d.]+)/],
      ["Firefox", /(?:Firefox|FxiOS)[\/ ]([\d.]+)/],
      ["Chrome", /(?:Chrome|CriOS)[\/ ]([\d.]+)/],
      ["Safari", /Version[\/ ]([\d.]+).*Safari/],
    ];
    let browser = "";
    let browserName = "";
    for (const [name, re] of rules) {
      const m = s.match(re);
      if (m) {
        browserName = name;
        browser = `${name} ${m[1].split(".")[0]}`;
        break;
      }
    }
    // Fallback: field mein sirf plain naam ho, jaise "Chrome" / "Brave"
    if (!browser) {
      const m = s.match(/edge|opera|firefox|chrome|safari|brave/i);
      if (m) {
        browserName = m[0].charAt(0).toUpperCase() + m[0].slice(1).toLowerCase();
        browser = browserName;
      }
    }

    return {
      label: [browser || "Unknown browser", os].filter(Boolean).join(" • "),
      mobile: /Android|iPhone|iPad|iOS/i.test(s),
      browser,
      browserName,
      os,
    };
  };

  const parsePrefs = (prefs) => {
    if (!prefs) return {};
    if (typeof prefs === "string") {
      try { prefs = JSON.parse(prefs); } catch (e) { return {}; }
    }
    return prefs && typeof prefs === "object" ? prefs : {};
  };

  const getStatusBucket = (status = "", prefs = null) => {
    const s = String(status || "").toLowerCase();
    if (!s) return "other";
    if (/(accept|allow|grant|agree|consent)/.test(s)) return "accepted";
    if (/(reject|declin|deny|disallow|revok|opt[- ]?out|no)/.test(s)) return "rejected";
    if (s === "custom") {
      const p = parsePrefs(prefs);
      const vals = Object.values(p);
      if (vals.length) return vals.some((v) => v === true) ? "accepted" : "rejected";
      return "accepted";
    }
    return "other";
  };

  /* ------------------------------ normalize ------------------------------ */
  const normalizeDpdpRecord = (r) => ({
    id: r.id ? `dpdp-${r.id}` : `dpdp-${r.session_id || Math.random().toString(36).slice(2, 9)}`,
    record_type: "dpdp",
    app_name: r.app_name || "DPDP Consent Banner",
    consent_status: r.consent_status || r.status || r.consent || "N/A",
    preferences: r.preferences || null,
    created_at: r.created_at || r.ts || r.timestamp || null,
    expires_at: r.expires_at || null,
    source_url: r.source_url || "",
    ip: getRecordIp(r),
    user_agent: getRecordUA(r),
    email: r.email || r.user_email || r.userEmail || "",
    session_id: r.session_id || "",
  });

  /* ------------------------------ data load ------------------------------ */
  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = getTenantId();
      let url = `${API_BASE}${DPDP_CONSENT_ENDPOINT}?page=${page}&page_size=${perPage}`;
      if (tenantId) url += `&tenant_id=${encodeURIComponent(tenantId)}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch DPDP consents");
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.dpdp_consents || data.consents || data.data || [];
      const normalized = rows.map(normalizeDpdpRecord);
      setRecords(normalized);
      setTotalItems(data.total || normalized.length || 0);
      setTotalPages(data.total_pages || Math.ceil((data.total || normalized.length || 0) / perPage) || 1);
    } catch (e) {
      setError("Failed to load consent records");
      setRecords([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, search]);

  /* ------------------------------ filtering ------------------------------ */
  const displayRecords = useMemo(() => {
    const text = search.toLowerCase();
    const range = DATE_RANGES.find((d) => d.value === dateRange);
    const since = range && range.days ? Date.now() - range.days * 24 * 60 * 60 * 1000 : null;

    return records.filter((r) => {
      if (typeFilter !== "all" && r.record_type !== typeFilter) return false;

      if (since) {
        const d = parseDate(r.created_at);
        if (!d || d.getTime() < since) return false;
      }

      return (
        (r.app_name || "").toLowerCase().includes(text) ||
        (r.consent_status || "").toLowerCase().includes(text) ||
        (r.source_url || "").toLowerCase().includes(text) ||
        (r.ip || "").toLowerCase().includes(text) ||
        (r.user_agent || "").toLowerCase().includes(text)
      );
    });
  }, [records, search, dateRange, typeFilter]);

  const summary = useMemo(() => {
    const acc = { total: 0, accepted: 0, rejected: 0, other: 0, expired: 0 };
    records.forEach((r) => {
      acc.total += 1;
      acc[getStatusBucket(r.consent_status, r.preferences)] += 1;
      if (isExpired(r)) acc.expired += 1;
    });
    return acc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [records]);

  /* ------------------------------ actions ------------------------------ */
  const openNewConsentRequest = () => {
    const baseUrl = window.location.origin;
    const tenantId = user?.tenant_id || user?.tenantId || localStorage.getItem("tenant_id") || "1";
    const popup = window.open(`${baseUrl}/webapp-consent?app=Portal`, "_blank");
    const initPayload = { type: "INIT_CONSENT", email: user?.email || "", tenant_id: tenantId, force: 1, redirect: baseUrl };
    try { if (popup && !popup.closed) popup.postMessage(initPayload, "*"); } catch (e) { /* ignore */ }
    const iv = setInterval(() => {
      try { if (popup && !popup.closed) popup.postMessage(initPayload, "*"); } catch (e) { /* ignore */ }
    }, 400);
    setTimeout(() => clearInterval(iv), 4000);
  };

  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const head = ["#", "Domain", "URL", "IP", "User Agent", "Email", "Consent Date", "Expires", "Consent Type", "Preferences", "Status"];
    const lines = displayRecords.map((r, i) =>
      [
        i + 1, r.app_name, r.source_url, getRecordIp(r), getRecordUA(r), r.email,
        formatDate(parseDate(r.created_at)), formatDate(getExpires(r)), r.consent_status,
        JSON.stringify(parsePrefs(r.preferences)), isExpired(r) ? "Expired" : "Active",
      ].map(esc).join(",")
    );
    const blob = new Blob([[head.map(esc).join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `consent-banner-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  /* ------------------------------ small UI pieces ------------------------------ */
  const StatusPill = ({ r }) => {
    const bucket = getStatusBucket(r.consent_status, r.preferences);
    const label = r.consent_status || "N/A";
    if (bucket === "accepted")
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle2 size={14} /> {label}
        </span>
      );
    if (bucket === "rejected")
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <XCircle size={14} /> {label}
        </span>
      );
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        <AlertTriangle size={14} /> {label}
      </span>
    );
  };

  const ActivePill = ({ expired }) =>
    expired ? (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Expired
      </span>
    ) : (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Active
      </span>
    );

  const PrefChips = ({ prefs, max = 2 }) => {
    const entries = Object.entries(parsePrefs(prefs));
    if (!entries.length) return <span className="text-xs text-gray-400">—</span>;
    const shown = entries.slice(0, max);
    const extra = entries.length - shown.length;
    return (
      <div className="flex flex-wrap gap-1.5">
        {shown.map(([k, v]) => (
          <span
            key={k}
            title={`${k}: ${String(v)}`}
            className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${v ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}
          >
            {k}
          </span>
        ))}
        {extra > 0 && <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">+{extra}</span>}
      </div>
    );
  };

  const prefsCount = (prefs) => {
    const vals = Object.values(parsePrefs(prefs));
    return vals.length ? `${vals.filter((v) => v === true).length} / ${vals.length}` : "—";
  };

  const siteIcon = (r) => {
    const host = hostOf(r.source_url, "");
    const local = /localhost|127\.0\.0\.1/.test(host || r.source_url || "");
    const ua = parseUA(getRecordUA(r));
    const BrowserComp = getBrowserComponent(ua.browserName || ua.browser);

    if (BrowserComp) {
      return (
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-50 border border-gray-200 shadow-sm shrink-0"
          title={ua.label || ua.browserName}
        >
          <BrowserComp size={22} />
        </div>
      );
    }

    const Icon = local ? Monitor : Globe;
    return (
      <div
        className={`w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0 ${local ? "bg-orange-400" : "bg-blue-500"}`}
        title={local ? "Localhost" : "Web"}
      >
        <Icon size={20} />
      </div>
    );
  };

  const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition disabled:opacity-60";

  /* ------------------------------ render ------------------------------ */
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-100 px-6 py-4 flex flex-col xl:flex-row xl:items-center gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
            <ShieldCheck size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 leading-tight">Consent Banner Tracker</h2>
            <p className="text-sm text-gray-500">View and manage DPDP consent banner records and preferences</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 xl:ml-auto">
          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by app, domain, IP, user agent, or consent type..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="tour-target-cookie-search w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {/* Date range */}
          <div className="relative">
            <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="appearance-none pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {DATE_RANGES.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>

          <button
            onClick={() => setView(view === "list" ? "grid" : "list")}
            className={`tour-target-view-toggle ${btn} bg-white border border-gray-200 text-gray-700 hover:bg-gray-100`}
          >
            {view === "list" ? <><LayoutGrid size={16} /> Grid</> : <><List size={16} /> List</>}
          </button>
          <button onClick={load} className={`tour-target-cookie-refresh ${btn} bg-white border border-gray-200 text-blue-600 hover:bg-gray-100`}>
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* STAT CARDS + TOP ACTIONS */}
        <div className="flex flex-col xl:flex-row xl:items-center gap-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-1">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 flex items-center gap-3">
              <CheckCircle2 size={34} className="text-emerald-600 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-emerald-800">Total Consents</div>
                <div className="text-3xl font-bold text-gray-900">{totalItems.toLocaleString("en-IN")}</div>
                <div className="text-xs text-gray-500">Across all applications</div>
              </div>
              <Users size={28} className="text-emerald-600" />
            </div>

            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 flex items-center gap-3">
              <CheckCircle2 size={34} className="text-blue-500 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-blue-800">Accepted</div>
                <div className="text-3xl font-bold text-gray-900">{summary.accepted}</div>
                <div className="text-xs text-gray-500">
                  Rejected: {summary.rejected} • Other: {summary.other}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-orange-200 bg-orange-50/60 p-4 flex items-center gap-3">
              <AlertTriangle size={34} className="text-orange-500 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-orange-800">Expired / Variance</div>
                <div className="text-3xl font-bold text-gray-900">{summary.expired + summary.other}</div>
                <div className="text-xs text-gray-500">Requires attention</div>
              </div>
              <AlertTriangle size={28} className="text-orange-400" />
            </div>
          </div>

          {/* <div className="flex items-center gap-3">
            <button onClick={exportCsv} className={`${btn} bg-white border border-gray-200 text-blue-600 hover:bg-gray-100`}>
              <Download size={16} /> Export
            </button>
            <button onClick={openNewConsentRequest} className={`${btn} bg-blue-600 hover:bg-blue-700 text-white`}>
              <Plus size={16} /> Add Consent
            </button>
          </div> */}
        </div>

        {/* Messages */}
        {loading && <div className="text-gray-500 text-sm mb-4">Loading...</div>}
        {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
        {/* LIST (TABLE) VIEW */}
        {view === "list" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-x-auto">
            <table className="w-full text-sm min-w-[1100px]">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-700 font-semibold border-b border-gray-200">
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3">Domain / Application</th>
                  <th className="px-4 py-3">User / IP Address</th>
                  <th className="px-4 py-3">Consent Date &amp; Time</th>
                  <th className="px-4 py-3">Consent Type</th>
                  <th className="px-4 py-3">Preferences Accepted</th>
                  <th className="px-4 py-3">Preferences</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRecords.map((r, index) => {
                  const expired = isExpired(r);
                  const expiresDate = getExpires(r);
                  const ua = parseUA(getRecordUA(r));
                  const DeviceIcon = ua.mobile ? Smartphone : Monitor;
                  const rowNo = (page - 1) * perPage + index + 1;
                  const isOpen = expandedId === r.id;
                  return (
                    <React.Fragment key={r.id || index}>
                      <tr className="border-b border-gray-100 hover:bg-gray-50/70 align-middle">
                        <td className="px-4 py-3 text-gray-700">{rowNo}</td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {siteIcon(r)}
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">
                                {hostOf(r.source_url, r.app_name || "Unknown App")}
                              </div>
                              {r.source_url && (
                                <a href={r.source_url} target="_blank" rel="noreferrer" className="text-xs text-gray-500 hover:text-blue-600 break-all">
                                  {r.source_url}
                                </a>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-gray-800">
                            <Users size={14} className="text-gray-400" />
                            <span className="break-all">{getRecordIp(r) || r.email || "—"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <DeviceIcon size={14} className="text-gray-400" /> {ua.label}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-gray-800">
                            <Calendar size={14} className="text-gray-400" />
                            {formatDate(parseDate(r.created_at)) || "—"}
                          </div>
                          {expiresDate && (
                            <div className={`text-xs mt-1 ml-6 ${expired ? "text-red-600" : "text-gray-500"}`}>
                              Expires: {formatDate(expiresDate)}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-3"><StatusPill r={r} /></td>
                        <td className="px-4 py-3 text-gray-700">{prefsCount(r.preferences)}</td>
                        <td className="px-4 py-3"><PrefChips prefs={r.preferences} /></td>
                        <td className="px-4 py-3"><ActivePill expired={expired} /></td>

                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setExpandedId(isOpen ? null : r.id)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                            title="View details"
                            aria-expanded={isOpen}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-gray-50 border-b border-gray-100">
                          <td />
                          <td colSpan={8} className="px-4 py-3 text-xs text-gray-600">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                              <div><strong>App:</strong> {r.app_name}</div>
                              <div><strong>Email:</strong> {r.email || "—"}</div>
                              <div><strong>IP:</strong> {getRecordIp(r) || "—"}</div>
                              <div><strong>Session:</strong> {r.session_id || "—"}</div>
                              <div className="md:col-span-2 break-words"><strong>User Agent:</strong> {getRecordUA(r) || "—"}</div>
                              <div className="md:col-span-2"><strong>All preferences:</strong></div>
                              <div className="md:col-span-2"><PrefChips prefs={r.preferences} max={50} /></div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="px-4 py-3 text-xs text-gray-500 border-t border-gray-100">
              Showing {displayRecords.length ? (page - 1) * perPage + 1 : 0} - {(page - 1) * perPage + displayRecords.length} of {totalItems} entries
            </div>
          </div>
        )}

        {/* GRID (CARD) VIEW */}
        {view === "grid" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {displayRecords.map((r, index) => {
              const expired = isExpired(r);
              const expiresDate = getExpires(r);
              const ua = parseUA(getRecordUA(r));
              return (
                <div
                  key={r.id || index}
                  className={`bg-white rounded-xl p-5 border shadow-sm hover:shadow-md transition ${expired ? "border-red-300 bg-red-50" : "border-gray-200"}`}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      {siteIcon(r)}
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{hostOf(r.source_url, r.app_name || "Unknown App")}</div>
                        <div className="text-xs text-gray-500">{formatDate(parseDate(r.created_at))}</div>
                      </div>
                    </div>
                    <StatusPill r={r} />
                  </div>

                  {r.source_url && (
                    <a href={r.source_url} target="_blank" rel="noreferrer" className="block text-xs text-blue-600 underline mb-2 break-all">
                      {r.source_url}
                    </a>
                  )}

                  <div className="text-xs text-gray-500 space-y-1">
                    {getRecordIp(r) && <div><strong>IP:</strong> {getRecordIp(r)}</div>}
                    <div><strong>Device:</strong> {ua.label}</div>
                    {r.email && <div><strong>Email:</strong> {r.email}</div>}
                    {expiresDate && (
                      <div className={expired ? "text-red-600" : ""}>
                        <strong>Expires:</strong> {formatDate(expiresDate)} {expired ? "(expired)" : ""}
                      </div>
                    )}
                  </div>

                  <div className="mt-3 flex items-center justify-between">
                    <PrefChips prefs={r.preferences} />
                    <ActivePill expired={expired} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        <CommonPagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          pageSize={perPage}
          onPageChange={setPage}
          onPageSizeChange={setPerPage}
          loading={loading}
        />

        {/* Empty State */}
        {!loading && displayRecords.length === 0 && (
          <div className="mt-10 text-center text-gray-500">
            No matching records found. Try a different search or date range.
          </div>
        )}
      </div>
    </div>
  );
}

export default CookiesNotifyPage;